import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { AccountSidebar } from '@/components/feature/AccountSidebar';
import Breadcrumb, { BreadcrumbItem } from '@/components/common/Breadcrumb';

export type { BreadcrumbItem };

export interface AccountLayoutProps {
  /** 子页面内容插槽 */
  children: React.ReactNode;
  /** 当前选中的侧边栏路径 */
  activePath?: string;
  /** 面包屑导航配置 */
  breadcrumbs?: BreadcrumbItem[];
  /** 页面标题 (可选) */
  title?: string;
  /** 页面副标题 (可选) */
  subtitle?: string;
  /** 页面右上角额外的操作按钮/元素 (可选) */
  headerAction?: React.ReactNode;
  /** 最外层容器额外类名 */
  className?: string;
  /** 主内容区域容器额外类名 */
  contentClassName?: string;
}

/**
 * 账号中心统一 Layout 布局组件
 * 统一控制 Fixed 导航栏顶距离避让 (pt-28 sm:pt-32)，左侧菜单，及页脚
 */
export const AccountLayout: React.FC<AccountLayoutProps> = ({
  children,
  activePath,
  breadcrumbs,
  title,
  subtitle,
  headerAction,
  className = '',
  contentClassName = '',
}) => {
  return (
    <div className={`min-h-screen flex flex-col bg-slate-50 ${className}`}>
      {/* 顶部固定导航栏 */}
      <Navbar />

      {/* 主体内容区，pt-16 sm:pt-20 防止固定定位导航栏遮挡顶部并保持舒适间距 */}
      <main className="flex-1 pt-16 sm:pt-20 pb-12 sm:pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* 面包屑导航 */}
          {breadcrumbs && breadcrumbs.length > 0 && (
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...breadcrumbs,
              ]}
              className="mb-2"
            />
          )}

          {/* 统一的页面头部大标题区域 (若传入 title) */}
          {(title || headerAction) && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
              <div>
                {title && <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h1>}
                {subtitle && <p className="text-sm text-slate-500 mt-1">{subtitle}</p>}
              </div>
              {headerAction && <div className="flex-shrink-0">{headerAction}</div>}
            </div>
          )}

          {/* 侧边栏与页面主体 */}
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
            {/* 左侧账号通用侧边栏 */}
            <AccountSidebar activePath={activePath} />

            {/* 右侧业务主面板 */}
            <div className={`flex-1 min-w-0 ${contentClassName}`}>{children}</div>
          </div>
        </div>
      </main>

      {/* 页脚 */}
      <Footer />
    </div>
  );
};

export default AccountLayout;
